'use client';

import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { usePrevious } from 'react-use';
import io from 'socket.io-client';
import { HMSRoomState, selectIsConnectedToRoom, selectPeers, selectRoomState, useHMSStore } from '@100mslive/react-sdk';
import { Flex } from '@100mslive/roomkit-react';
import { Beam } from './Pages/Beam';
import { MainViewConnector } from '../layouts/mainViewConnector';
import { NetworkConnectionLost } from './Pages/components/NetworkConnectionLost.jsx';
import FullPageProgress from './FullPageProgress';
import { generateUrl } from '../utils/helper';
import { DEFAULT_FORM } from '../common/constants';
export var socket;

const Conference = ({ disconnect, isConnected }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const { roomId, role } = useParams();
    const roomState = useHMSStore(selectRoomState);
    const prevState = usePrevious(roomState);
    const isConnectedToRoom = useHMSStore(selectIsConnectedToRoom);
    const [form, setForm] = useState(location?.state || null);
    const [callEnd, setCallEnd] = useState(false);
    const [livKitRoomState, setLivKitRoomState] = useState('');
    const [finalCallEnd, setFinalCallEnd] = useState(false);
    const [timer, setTimer] = useState(0);
    const [startTimer, setStartTimer] = useState(false);
    const [isPlaying, setIsPlaying] = useState(false);
    const [allMessages, setAllMessages] = useState(false);
    const [isNetworkError, setIsNetworkError] = useState(false);
    const [onlineStatus, setOnlineStatus] = useState(true);
    const [displayInput, setDisplayInput] = useState({
        message: '',
        questionType: '',
        displayText: '',
        isDisabled: false,
        audioData: '',
    });
    const [codeEditorForm, setCodeEditorForm] = useState(DEFAULT_FORM);
    const [isSubmitModalOpen, setIsSubmitModalOpen] = useState(false);
    const [changeInCode, setChangeInCode] = useState(false);
    const [answerTime, setAnswerTime] = useState(0);
    const microsoftKeys = location?.state?.microsoftKeys ?? [];

    const peers = useHMSStore(selectPeers);
    const candidatePeer = peers.filter((peer) => peer.roleName === 'candidate');

    useEffect(() => {
        if (isConnected && livKitRoomState === 'disconnected') {
            handleEndCall();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [livKitRoomState, isConnected]);

    useEffect(() => {
        if (isConnected) {
            const newTimer = form?.codingEnabled ? 3600 : 2700;
            console.log(`[Timer] Setting timer: ${newTimer}`); // Debugging log
            setTimer(newTimer);
            setStartTimer(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isConnected, form?.codingEnabled, roomId, role]);

    useEffect(() => {
        if (role === 'candidate') {
            let countdown;
            if (startTimer) {
                countdown = setInterval(() => {
                    setTimer((prev) => {
                        if (prev <= 1) {
                            clearInterval(countdown);
                            handleEndCall();
                            return 0;
                        }
                        return prev - 1;
                    });
                }, 1000);
            }

            return () => clearInterval(countdown);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [startTimer, role]);

    useEffect(() => {
        if (socket && role === 'candidate') updateSocket('timer', timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [timer, socket]);

    console.log('[State] callEnd:', callEnd);

    const socketDataHandlers = {
        timer: setTimer,
        isPlaying: setIsPlaying,
        answerTime: setAnswerTime,
        displayInput: setDisplayInput,
        codeEditorForm: setCodeEditorForm,
        changeInCode: setChangeInCode,
        isSubmitModalOpen: setIsSubmitModalOpen,
        allMessages: setAllMessages,
    };

    const updateSocket = (type, value) => {
        try {
            console.log(`[Socket] Update ${type}: ${value} `);
            socket?.emit('codeWritten', { type, value }, roomId);
        } catch (error) {}
    };

    useEffect(() => {
        let socketInstance;

        console.log('[Socket] Initializing socket connection');
        const initializeSocket = () => {
            if (roomId) {
                if (socket && socket.disconnected) {
                    socket.close();
                    socket = null;
                }

                socketInstance = io(generateUrl('socket'), {
                    transports: ['websocket'],
                    reconnection: true,
                    reconnectionAttempts: Infinity, // allow unlimited attempts
                    reconnectionDelay: 2000, // 2 sec retry
                });

                socket = socketInstance;

                socket.emit('joinRoom', { roomId, name: role });

                // Setup listeners
                socket.on('connect', () => {
                    console.log('[Socket] Connected');
                });

                socket.on('disconnect', (reason) => {
                    console.warn('[Socket] Disconnected:', reason);
                });

                socket.on('reconnect_attempt', (attempt) => {
                    console.log(`[Socket] Reconnect attempt #${attempt}`);
                });

                socket.on('receiveCode', (data) => {
                    if (role === 'beam' && socketDataHandlers[data?.type]) {
                        console.log('[Socket] Received code update:', data);
                        socketDataHandlers[data.type](data.value);
                    }
                });
            }
        };

        initializeSocket();

        return () => {
            if (socketInstance) {
                socketInstance.off('receiveCode');
                socketInstance.disconnect();
            }
        };
    }, [roomId, role]);

    const handleEndCall = async () => {
        console.log('[Call] Ending call');
        setCallEnd(true);
        console.log('[Call] Call end state updated');
        setTimeout(async () => {
            if (role === 'candidate') await disconnect();
            console.log('[Call] Disconnected after call end');
        }, 2000);
    };

    useEffect(() => {
        if (role === 'candidate') {
            if (location?.state) {
                setForm(location?.state);
                if (!(location?.state?.otpVerified && location?.state?.instructionAccepted)) {
                    const meetingURL = `/room/verify/${role}/${roomId}`;
                    navigate(meetingURL);
                }
            } else if (roomId) {
                // No router state - e.g. the interview link was reopened directly, or a
                // refresh dropped history state. Rehydrate through the preview screen,
                // which re-verifies the room and, for a resumable session, rejoins
                // straight away instead of getting stuck here or forcing OTP again.
                navigate(`/room/preview/${role}/${roomId}`);
            }
        }
    }, [location?.state, navigate, roomId, role]);

    useEffect(() => {
        if (!roomId) {
            navigate(`/`);
            return;
        }
        if (
            !prevState &&
            !(roomState === HMSRoomState.Connecting || roomState === HMSRoomState.Reconnecting || isConnectedToRoom)
        ) {
            navigate(`/room/preview/${role}/${roomId}`, { state: form });
        }
    }, [isConnectedToRoom, prevState, roomState, navigate, role, roomId, form]);

    useEffect(() => {
        const handleTimeout = () => {
            if (!onlineStatus) setIsNetworkError(true);
        };
        const timeoutId = setTimeout(handleTimeout, 10000);
        return () => clearTimeout(timeoutId);
    }, [onlineStatus]);

    const offlineCallback = () => setOnlineStatus(false);
    const onlineCallback = () => setOnlineStatus(true);

    useEffect(() => {
        window.addEventListener('offline', offlineCallback);
        window.addEventListener('online', onlineCallback);
        return () => {
            window.removeEventListener('offline', offlineCallback);
            window.removeEventListener('online', onlineCallback);
        };
    }, []);

    useEffect(() => {
        if (onlineStatus && isNetworkError) {
            console.log('Network Called End');
            setFinalCallEnd(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [onlineStatus, isNetworkError]);

    if (!isConnectedToRoom || !form) return <FullPageProgress />;

    if (isNetworkError && role === 'candidate') return <NetworkConnectionLost inInterviewRoom />;

    if (role === 'beam') {
        return (
            <Flex css={{ size: '100%', overflow: 'hidden' }} direction="column">
                <Beam
                    timer={timer}
                    candidatePeer={candidatePeer}
                    form={form}
                    data={displayInput}
                    isPlaying={isPlaying}
                    codeEditorForm={codeEditorForm}
                    changeInCode={changeInCode}
                    setIsSubmitModalOpen={setIsSubmitModalOpen}
                    isSubmitModalOpen={isSubmitModalOpen}
                    answerTime={answerTime}
                    allMessages={allMessages}
                />
            </Flex>
        );
    }

    return (
        <MainViewConnector
            form={form}
            timer={timer}
            candidatePeer={candidatePeer}
            callEnd={callEnd}
            finalCallEnd={finalCallEnd}
            socket={socket}
            codeEditorForm={codeEditorForm}
            setCodeEditorForm={setCodeEditorForm}
            changeInCode={changeInCode}
            setChangeInCode={setChangeInCode}
            onlineStatus={onlineStatus}
            updateSocket={updateSocket}
            displayInput={displayInput}
            setDisplayInput={setDisplayInput}
            setLivKitRoomState={setLivKitRoomState}
            microsoftKeys={microsoftKeys}
        />
    );
};

export default Conference;
